import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

// Production backend URL - Travelers API (Traveling BABA)
// API Docs: https://travaling-babaa-2856d78a73bf.herokuapp.com/docs
const API_BASE_URL: string = process.env.REACT_APP_API_URL || 'https://travaling-babaa-2856d78a73bf.herokuapp.com';

// Create axios instance with proper configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging and adding common headers
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    // You can add auth tokens here if needed
    // config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error: AxiosError): Promise<AxiosError> => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,
  (error: AxiosError): Promise<AxiosError> => {
    // Handle common errors
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      if (status === 401) {
        console.error('Unauthorized request');
      } else if (status === 404) {
        console.error('Resource not found');
      } else if (status >= 500) {
        console.error('Server error');
      }
    } else if (error.request) {
      // Request made but no response received
      console.error('Network error - no response received');
    }
    return Promise.reject(error);
  }
);

/**
 * Handle API errors consistently
 */
export const handleApiError = (error: unknown, defaultMessage: string): Error => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ detail?: string; message?: string }>;
    if (axiosError.response) {
      // Server responded with error
      const message = 
        axiosError.response.data?.detail || 
        axiosError.response.data?.message || 
        defaultMessage;
      return new Error(message);
    } else if (axiosError.request) {
      // No response received
      return new Error('No response from server. Please check your connection.');
    }
  }
  // Generic error
  return error instanceof Error ? error : new Error(defaultMessage);
};

// Countries API
export const countriesAPI = {
  /**
   * Get paginated countries list
   * GET /api/v1/countries/
   */
  getCountries: async (
    page: number = 1, 
    pageSize: number = 20, 
    config?: AxiosRequestConfig
  ) => {
    try {
      return await api.get('/api/v1/countries/', {
        params: { page, page_size: pageSize },
        ...config,
      });
    } catch (error) {
      throw handleApiError(error, 'Failed to fetch countries');
    }
  },

  /**
   * Get country by name
   * GET /api/v1/countries/{countryName}/
   */
  getCountryByName: async (countryName: string, config?: AxiosRequestConfig) => {
    try {
      return await api.get(`/api/v1/countries/${encodeURIComponent(countryName)}/`, config);
    } catch (error) {
      throw handleApiError(error, `Failed to fetch country: ${countryName}`);
    }
  },
};

/**
 * Get full URL for API resources (images, etc.)
 */
export const getApiBaseUrl = (): string => API_BASE_URL;

export default api;
